import {
  getPlayersSearch,
  getPlayersSearchQueryKey,
} from "@aura-frontend/omnibus-az-client";
import { useInfiniteQuery } from "@tanstack/react-query";

import { addOrgPrefix } from "@/lib/utils";

/**
 * Fetches players from the API
 *
 * @param organizationId - The organization ID to fetch players for.
 * @param continueString - The continue string to fetch the next page of players.
 * @param debouncedSearchValue - The debounced search value to fetch players for.
 * @param enabled - Whether the query is enabled.
 * @returns The players data, fetchNextPage, hasNextPage, isFetchingNextPage, status, and error.
 */
export const useAvailablePlayers = ({
  organizationId,
  continueString,
  debouncedSearchValue,
  enabled,
}: {
  organizationId: string;
  continueString: string;
  debouncedSearchValue: string;
  enabled: boolean;
}) => {
  const queryKeyValue = getPlayersSearchQueryKey({
    query: {
      visibilityGroup: addOrgPrefix(organizationId),
      continue: continueString,
      name: debouncedSearchValue || undefined,
    },
  });

  const {
    data: playersData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    error,
  } = useInfiniteQuery({
    queryKey: queryKeyValue,
    queryFn: async ({ queryKey, pageParam }) => {
      const { data } = await getPlayersSearch({
        query: {
          ...queryKey[0].query,
          continue: pageParam as string,
        },
        throwOnError: true,
      });
      return data;
    },
    getNextPageParam: (lastPage) => lastPage.continue,
    initialPageParam: "",
    enabled,
  });

  return {
    playersData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    error,
  };
};
